import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AppContextInterface, useAppContext } from '../../contexts/auth';
import useApiCall from '../../hooks/useApiCall';
import useErrors from '../../hooks/useErrors';
import useGetGeneralAppData from '../../hooks/useGetGeneralAppData';
import CompanyService from '../../services/CompanyService';
import CompanySettingsService from '../../services/CompanySettingsService';
import companyTokenService from '../../services/companyTokenService';
import formatCnpj from '../../utils/formatCnpj';
import formatPhone from '../../utils/formatPhone';
import isCnpjValid from '../../utils/isCnpjValid';
import onlyNumbers from '../../utils/onlyNumbers';
import { DadosConfig, Email, ServicosContrato, SettingsFromApiI } from './Settings/types';
import { CompanyDataType } from './types';

export default function useCompany() {
  const [isLoading, setIsLoading] = useState(false);
  const [doesApiHasError, setDoesApiHasError] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyDataType>({} as CompanyDataType);
  const [canShowServicesButton, setCanShowServicesButton] = useState(false);

  const [editCompanyModalShow, setEditCompanyModalShow] = useState(false);
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [fantasyName, setFantasyName] = useState('');
  const [cnpjPagto, setCnpjPagto] = useState('');
  const [pagadorNFSeparada, setPagadorNFSeparada] = useState(false);
  const [employeesQty, setEmployeesQty] = useState<string | number>('');

  const [lockAccessModalShow, setLockAccessModalShow] = useState(false);

  const [currentSettingsData, setCurrentSettingsData] = useState({} as {
    dadosConfig: DadosConfig
    emails: Email[]
    servicosContrato: ServicosContrato[]
  });

  const [servicesModalShow, setServicesModalShow] = useState(false);
  const [servicesList, setServicesList] = useState<{ value: number; label: string }[]>([]);
  const [selectedServices, setSelectedServices] = useState<{ value: number; label: string }[]>([]);
  const [isFirstSetup, setIsFirstSetup] = useState(false);

  const [companyToken, setCompanyToken] = useState('');

  const { userData, selectedCompany }: AppContextInterface = useAppContext();
  const { userProfile, userCode } = userData;
  const { isCustomer } = useGetGeneralAppData();

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();
  const { apiCall } = useApiCall();

  const isReadyToEdit = errors.length === 0
    && !!companyPhone
    && !!employeesQty
    && !!companyName.trim()
    && !!fantasyName.trim()
    && (!cnpjPagto.trim() || isCnpjValid(cnpjPagto));

  function handleCompanyNameChange(event: ChangeEvent<HTMLInputElement>) {
    setCompanyName(event.target.value);
    if (!event.target.value.trim()) {
      setError({ field: 'companyName', message: 'Razão social é obrigatória!' });
    } else {
      removeError('companyName');
    }
  }

  function handleFantasyNameChange(event: ChangeEvent<HTMLInputElement>) {
    setFantasyName(event.target.value);
    if (!event.target.value.trim()) {
      setError({ field: 'fantasyName', message: 'Nome fantasia é obrigatório!' });
    } else {
      removeError('fantasyName');
    }
  }

  function handleCnpjPagtoChange(event: ChangeEvent<HTMLInputElement>) {
    const formatted = formatCnpj(event.target.value.slice(0, 20));
    setCnpjPagto(formatted);
    if (formatted && !isCnpjValid(formatted)) {
      setError({ field: 'cnpjPagto', message: 'Informe um CNPJ de pagamento válido!' });
    } else {
      removeError('cnpjPagto');
    }
  }

  function handleCompanyPhoneChange(event: ChangeEvent<HTMLInputElement>) {
    setCompanyPhone(formatPhone(event.target.value));

    if (!event.target.value) {
      setError({ field: 'companyCellphone', message: 'Telefone é obrigatório!' });
    } else if (event.target.value.length < 9) {
      setError({ field: 'companyCellphone', message: 'Telefone inválido' });
    } else {
      removeError('companyCellphone');
    }
  }

  function handleEmployeesQtyChange(event: ChangeEvent<HTMLInputElement>) {
    setEmployeesQty(onlyNumbers(event.target.value));
    if (!event.target.value) {
      setError({ field: 'employeesQty', message: 'Número de funcionários é obrigatório!' });
    } else if (Number(event.target.value) === 0) {
      setError({ field: 'employeesQty', message: 'Número de funcionários não pode ser 0' });
    } else {
      removeError('employeesQty');
    }
  }

  const getCompanyData = useCallback(async () => {
    await apiCall({
      apiToCall: CompanyService.getCompanyData,
      onStartLoad: () => {
        setIsLoading(true);
        setDoesApiHasError(false);
      },
      onEndLoad: () => setIsLoading(false),
      queryParams: {
        codEmpresa: selectedCompany.value,
        codUsu: userCode,
        perfilUsu: userProfile,
      },
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar os dados da empresa. Por favor, tente novamente (${response.msg})`);
          setDoesApiHasError(true);
          return;
        }
        setCompanyPhone(`(${response.dadosEmpresa.telDDD}) ${response.dadosEmpresa.telNumero}`);
        setCompanyName(response.dadosEmpresa.razaoSoc ?? '');
        setFantasyName(response.dadosEmpresa.nomeFantasia ?? '');
        setCnpjPagto(response.dadosEmpresa.cnpjPagto || response.dadosEmpresa.cnpj || '');
        setPagadorNFSeparada(response.dadosEmpresa.pagadorNFSeparada ?? false);
        setEmployeesQty(response.dadosEmpresa.qtdFunc);
        const mappedCompanyData = {
          generalData: {
            cnpj: response.dadosEmpresa.cnpj,
            cnpjPagto: response.dadosEmpresa.cnpjPagto || response.dadosEmpresa.cnpj,
            pagadorNFSeparada: response.dadosEmpresa.pagadorNFSeparada ?? false,
            cnpjCorretoOriginal: response.dadosEmpresa.cnpjCorretoOriginal ?? '',
            fantasyName: response.dadosEmpresa.nomeFantasia,
            companyName: response.dadosEmpresa.razaoSoc,
            employeesQty: response.dadosEmpresa.qtdFunc,
            stateRegistration: response.dadosEmpresa.inscrEst,
            municipalRegistration: response.dadosEmpresa.inscrMun,
            companyPhone: `(${response.dadosEmpresa.telDDD}) ${response.dadosEmpresa.telNumero}`,
            companyCode: response.dadosEmpresa.codEmpresa,
          },
          companyAddress: {
            cep: response.dadosEmpresa.endCep,
            uf: response.dadosEmpresa.endUF,
            city: response.dadosEmpresa.endCidade,
            district: response.dadosEmpresa.endBairro,
            streetName: response.dadosEmpresa.endLogradouro,
            number: response.dadosEmpresa.endNumero,
            complement: response.dadosEmpresa.endComplemento,
          },
          billingAddress: {
            cep: response.dadosEmpresa.endFCep,
            uf: response.dadosEmpresa.endFUF,
            city: response.dadosEmpresa.endFCidade,
            district: response.dadosEmpresa.endFBairro,
            streetName: response.dadosEmpresa.endFLogradouro,
            number: response.dadosEmpresa.endFNumero,
            complement: response.dadosEmpresa.endFComplemento,
          },
          contactData: {
            name: response.dadosEmpresa.nomeContato,
            cpf: response.dadosEmpresa.cpf,
            cellphone: response.dadosEmpresa.telContato,
            email: response.dadosEmpresa.email,
          },
          isAccessUnlocked: response.dadosEmpresa.statusEmp === 'Ativa',
        };
        setCompanyData(mappedCompanyData);
      },
      catchMessage: 'Não foi possível carregar os dados da empresa. Por favor, tente novamente',
      catchAction: () => setDoesApiHasError(true),
    });
  }, [apiCall, selectedCompany.value, userCode, userProfile]);

  const editCompany = useCallback(async () => {
    await apiCall({
      apiToCall: CompanyService.editCompany,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
      },
      reqBody: JSON.stringify({
        codEmpresa: selectedCompany.value,
        razaoSocial: companyName.trim(),
        nomeFantasia: fantasyName.trim(),
        telDDD: Number(companyPhone.split(')')[0].slice(1, 3)),
        telNumero: companyPhone.split(')')[1].replaceAll(' ', ''),
        qtdFunc: employeesQty,
        cnpjPagto: cnpjPagto.trim() || companyData.generalData?.cnpj || '',
        pagadorNFSeparada,
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível editar os dados da empresa. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setEditCompanyModalShow(false);
        toast.success('Os dados da empresa foram alterados com sucesso!');
        getCompanyData();
      },
      catchMessage: 'Não foi possível editar os dados da empresa. Por favor, tente novamente'
    });
  }, [apiCall, companyData.generalData?.cnpj, companyName, companyPhone, cnpjPagto, employeesQty, fantasyName, getCompanyData, pagadorNFSeparada, selectedCompany.value, userCode, userProfile]);

  const lockAccess = useCallback(async () => {
    await apiCall({
      apiToCall: CompanyService.lockCompany,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: selectedCompany.value,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: async (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível bloquear o acessa da empresa. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setLockAccessModalShow(false);
        await getCompanyData();
        toast.success('O acesso da empresa foi bloqueado com sucesso!');
      },
      catchMessage: 'Não foi possível liberar o acesso da empresa. Por favor, tente novamente'
    });
  }, [apiCall, getCompanyData, selectedCompany.value, userCode, userProfile]);

  const unlockAccess = useCallback(async () => {
    await apiCall({
      apiToCall: CompanyService.unlockCompany,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: selectedCompany.value,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: async (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível liberar o acessa da empresa. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setLockAccessModalShow(false);
        await getCompanyData();
        toast.success('O acesso da empresa foi liberado com sucesso!');
      },
      catchMessage: 'Não foi possível liberar o acesso da empresa. Por favor, tente novamente'
    });
  }, [apiCall, getCompanyData, selectedCompany.value, userCode, userProfile]);

  const getServicesOptions = useCallback(async () => {
    await apiCall({
      apiToCall: CompanyService.getServicesOptions,
      queryParams: { codEmpresa: selectedCompany.value, codUsu: userCode, perfilUsu: userProfile },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar a lista de serviços. Por favor, tente novamente (${response.msg})`);
          return;
        }
        const mappedServices = response.servicos.map((service: { codServ: number; nomeServico: string }) => ({
          value: service.codServ,
          label: service.nomeServico,
        }));
        setServicesList(mappedServices);
      },
    });
  }, [apiCall, selectedCompany.value, userCode, userProfile]);

  const loadCompanySettings = useCallback(async () => {
    let integratorId = 0;

    await apiCall({
      apiToCall: CompanySettingsService.getCompanySettings,
      queryParams: { codEmpresa: selectedCompany.value, codUsu: userCode, perfilUsu: userProfile },
      catchMessage: 'Houve um erro ao carregar os dados de configuração',
      actionAfterResponse: (response: SettingsFromApiI) => {
        const defaultSettings = {
          'dadosConfig': {
            'alteraDadosLT': false,
            'emailAceite': false,
            'emailExtPA': false,
            'emailRotSemResultado': false,
            'utilizaTeto': false,
            'valorTeto': 0,
            'utilizaTetoValorPadraoEmp': false,
            'espelhamento': true,
            'usaVTInicial': false,
            'valVTInicial': 0,
            'usaFreta': false,
            'usaHorarioFretado': true,
            'usaLimKm': false,
            'usaMyLinkFull': false,
            'kmLimite': 0,
            'opcaoTextoCarta': 0,
            'envelopadora': 0,
            'nomeEnvelopadora': '',
            'textoCarta': '',
            'logoCarta': false,
            'tipoBaldeacao': 2 as 0 | 1 | 2,
            'regraVTBilSemTx': false,
            'regraBilSemIntegraRJ': false,
            'emailFuncWebC': false,
            'cartaLiberada': true,
            'reprocessaMyLink': false,
            'envioDadosWCAceite': false,
            'MyLinkLogo': false,
            'bloqueiaEmbCAF': false,
            'diasBloqueioCAF': 0,
            'conexaoFolhaPg': false,
            'codTecFolhaPg': 0,
            'nomeTecFolhaPg': '',
            'codParceiro': 0,
            'nomeParceiro': '',
            'codParceiro2': 0,
            'nomeParceiro2': '',
            'emailDirect': false,
            'notificaFuncDirect': false,
            'cartaEmBranco': false,
            'totalMaxReclama': 3 as 1 | 2 | 3,
            'distInicial': 1000,
            'emailLogo': false,
            urlArqLogo: '',
            urlDocWebC: '',
            tipoParceiro: false,
            codEmpresaParceiro: 0,
            temLogoEmail: true,
            verificaSalarioRJ: false,
            atualizaCodVTEnvelp: false,
            usaWebhook: false,
            regraVTSemTxAesa: false,
            acimaTetoBiasTag: false,
            usaWebhook2: false,
            usaWebhookForaAbrang: false,
            nomeIntegrador: '',
            codIntegrador: 0,
            particularidades: '',
            anexarComprovanteEnd: true,
            usaRodoviarioValZero: false,
            kmRodoviarioValZero: 0,
            geraNFSeparada: false,
            geraNFSeparadaMatriz: false,
          },
          'emails': [],
          servicosContrato: [],
        };
        if (!response.codigo) {
          toast.error('Ocorreu um erro ao carregar os dados de configuração', { toastId: 'loadCompanySettingsError' });
          setCurrentSettingsData(defaultSettings);
          setIsFirstSetup(true);
          setCanShowServicesButton(false);
          return;
        }
        setCanShowServicesButton(true);

        if (!response.codRetorno) {
          toast.info('Essa empresa ainda não teve suas configurações e serviços definidos, por favor, selecione os serviços habilitados e, após, configure a empresa.');
          setCurrentSettingsData(defaultSettings);
          setIsFirstSetup(true);
          return;
        }
        setIsFirstSetup(false);

        const { dadosConfig, emails, servicosContrato } = response;
        setCurrentSettingsData({ dadosConfig, emails, servicosContrato });
        setSelectedServices(servicosContrato?.map((service: { codServ: number; servico: string }) => ({
          value: service.codServ,
          label: service.servico,
        })));
        integratorId = dadosConfig.codIntegrador || dadosConfig.codParceiro;
      },
    });

    if (integratorId) {
      await apiCall({
        apiToCall: companyTokenService.getCompanyToken,
        queryParams: { companyId: selectedCompany.value, integratorId: integratorId?.toString() },
        actionAfterResponse: (response) => {
          setCompanyToken(response.token || '');
        },
        catchMessage: 'Houve um erro ao carregar o token da empresa',
        catchAction() {
          setCompanyToken('');
        },
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
      });
    }
  }, [apiCall, selectedCompany.value, userCode, userProfile]);

  const confirmServices = useCallback(async () => {
    await apiCall({
      apiToCall: CompanySettingsService.editOrSetCompanySettings,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: selectedCompany.value },
      reqBody: JSON.stringify({
        'codUsu': userCode,
        'perfilUsu': userProfile,
        'codEmpresa': selectedCompany.value,
        'tipoAcao': isFirstSetup ? 11 : 12,
        'listaEmails': !!currentSettingsData.emails && currentSettingsData.emails.length > 0,
        'dados': currentSettingsData.dadosConfig,
        'emailsCli': currentSettingsData.emails ?? [],
        servicosContrato: selectedServices.map((service) => ({ codServ: service.value })),
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: async (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível confirmar os seriços habilitados da empresa. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setServicesModalShow(false);
        await getCompanyData();

        if (!isCustomer) {
          await loadCompanySettings();
        }
        toast.success('Os serviços habilitados da empresa foram confirmados!');
      }
    });
  }, [apiCall, currentSettingsData.dadosConfig, currentSettingsData.emails, getCompanyData, isCustomer, isFirstSetup, loadCompanySettings, selectedCompany.value, selectedServices, userCode, userProfile]);

  function handleTryAgain() {
    getCompanyData();
  }

  useEffect(() => {
    if (selectedCompany.value) {
      getCompanyData();

      if (!isCustomer) {
        getServicesOptions();
        loadCompanySettings();
      }
    }
  }, [getCompanyData, getServicesOptions, isCustomer, loadCompanySettings, selectedCompany.value]);

  const isSomeCompanySelected = !!selectedCompany.value;

  return {
    isLoading,
    doesApiHasError,
    companyData,
    handleTryAgain,
    isSomeCompanySelected,
    userProfile,
    editCompanyModalShow,
    companyPhone,
    handleCompanyPhoneChange,
    companyName,
    handleCompanyNameChange,
    fantasyName,
    handleFantasyNameChange,
    cnpjPagto,
    handleCnpjPagtoChange,
    pagadorNFSeparada,
    setPagadorNFSeparada,
    employeesQty,
    handleEmployeesQtyChange,
    getErrorMessageByFieldName,
    setEditCompanyModalShow,
    editCompany,
    isReadyToEdit,
    setLockModalShow: setLockAccessModalShow,
    lockCompany: lockAccess,
    lockModalShow: lockAccessModalShow,
    unlockAccess,
    servicesModalShow,
    setServicesModalShow,
    servicesList,
    selectedServices,
    setSelectedServices,
    confirmServices,
    canShowServicesButton,
    companyToken,
  };
}
